import type { FeatureCollection } from "geojson";
import React, { useEffect, useRef } from "react";

import type { EstimateResponse, LatLng } from "../../../types/journey";
import { boundsFromCoords, boundsFromFeatureCollection } from "./bounds";
import { cameraForBounds, DEFAULT_BOUNDARY_PADDING, fitMapToBounds, flySmooth } from "./camera";

function getMapInstance(mapRef: React.RefObject<any>) {
  const cur = mapRef.current;
  return cur?.getMap?.() ?? cur;
}

export function useJourneyCamera(args: {
  mapRef: React.RefObject<any>;
  containerWidth: number;
  hasLegend: boolean;

  boundary: FeatureCollection | null;
  origin: LatLng | null;
  destination: LatLng | null;
  estimate: EstimateResponse | null;

  isEditingOrigin: boolean;
  isEditingDestination: boolean;

  suppressOrigin: boolean;
  suppressDestination: boolean;
  suppressRoute: boolean;
}) {
  const didInitialBoundaryFitRef = useRef(false);
  const defaultViewRef = useRef<{ center: [number, number]; zoom: number } | null>(null);

  // Initial boundary framing + capture default camera
  useEffect(() => {
    const map = getMapInstance(args.mapRef);
    if (!map || !args.boundary) return;

    if (args.isEditingOrigin || args.isEditingDestination) return;
    if (didInitialBoundaryFitRef.current) return;
    if (args.origin || args.destination || args.estimate?.route) return;

    const b = boundsFromFeatureCollection(args.boundary);
    if (!b) return;

    const cam = cameraForBounds(map, b, DEFAULT_BOUNDARY_PADDING);
    if (cam) defaultViewRef.current = cam;

    fitMapToBounds(map, b, { duration: 0, padding: DEFAULT_BOUNDARY_PADDING });

    didInitialBoundaryFitRef.current = true;
  }, [
    args.mapRef,
    args.boundary,
    args.origin,
    args.destination,
    args.estimate,
    args.isEditingOrigin,
    args.isEditingDestination,
  ]);

  useEffect(() => {
    const map = getMapInstance(args.mapRef);
    if (!map) return;

    const editing = args.isEditingOrigin || args.isEditingDestination;

    // Keep things calm during editing:
    // - if one visible marker, center it
    // - if none, go back to default view
    if (editing) {
      const visibleOrigin = Boolean(args.origin) && !args.suppressOrigin;
      const visibleDestination = Boolean(args.destination) && !args.suppressDestination;

      const focus = visibleOrigin ? args.origin : visibleDestination ? args.destination : null;

      if (focus) {
        flySmooth(map, {
          center: [focus.lng, focus.lat],
          zoom: 14,
          duration: 650,
        });
        return;
      }

      const dv = defaultViewRef.current;
      if (dv) {
        flySmooth(map, { center: dv.center, zoom: dv.zoom, duration: 650 });
      }
      return;
    }

    // Prefer route bounds when route exists and not suppressed
    const routeCoords = args.estimate?.route?.geometry?.coordinates ?? [];
    if (routeCoords.length >= 2 && !args.suppressRoute) {
      const b = boundsFromCoords(routeCoords);
      const padding = args.hasLegend
        ? { ...DEFAULT_BOUNDARY_PADDING, right: DEFAULT_BOUNDARY_PADDING.right + 280 }
        : DEFAULT_BOUNDARY_PADDING;

      fitMapToBounds(map, b, { duration: 700, padding });
      return;
    }

    // If origin+destination, fit both
    if (args.origin && args.destination && !args.suppressOrigin && !args.suppressDestination) {
      const b = boundsFromCoords([args.origin, args.destination]);
      const padding = args.hasLegend
        ? { ...DEFAULT_BOUNDARY_PADDING, right: DEFAULT_BOUNDARY_PADDING.right + 280 }
        : DEFAULT_BOUNDARY_PADDING;

      fitMapToBounds(map, b, { duration: 650, padding });
      return;
    }

    // Single visible marker
    const onlyOrigin = args.origin && !args.suppressOrigin ? args.origin : null;
    const onlyDestination = args.destination && !args.suppressDestination ? args.destination : null;
    const focus = onlyOrigin ?? onlyDestination;

    if (focus) {
      flySmooth(map, { center: [focus.lng, focus.lat], zoom: 14, duration: 650 });
      return;
    }

    // Reset
    const dv = defaultViewRef.current;
    if (dv) {
      flySmooth(map, { center: dv.center, zoom: dv.zoom, duration: 650 });
    }
  }, [
    args.mapRef,
    args.containerWidth,
    args.hasLegend,
    args.boundary,
    args.origin,
    args.destination,
    args.estimate,
    args.isEditingOrigin,
    args.isEditingDestination,
    args.suppressOrigin,
    args.suppressDestination,
    args.suppressRoute,
  ]);
}
